import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { parseISO } from 'date-fns';
import Swal from 'sweetalert2';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { SearchService } from 'src/app/services/search.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * OrdiniDaFirmareComponent — orders awaiting carrier signature.
 *
 * Two-step UX (single-table swap, Chunk D.1, Phase 1.5 v2.7.0):
 *   • List mode (chosenCarrier === null):
 *       All "orders-to-sign" in a Diakonix table. Click row → detail
 *       modal, click "Completa" → confirm-complete sub-modal.
 *   • Sign-batch mode (chosenCarrier set via courier dropdown):
 *       Table is replaced with a filtered list of orders for the
 *       selected carrier, with a checkbox column. Footer toolbar
 *       surfaces "Seleziona tutti" + "Genera Borderò". Click
 *       "← Torna alla lista" to exit back to list mode.
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up:
 *   • dropped ~30 console.log/debug,
 *   • dropped kendo `GridDataResult` + `LabelSettings` imports + ~25
 *     unused state fields (gridView, value, indeterminate, min, max,
 *     ordz, openOrders, setPercentLabel, range, dateBy, hiddenStyle,
 *     courier_name, order_carrier, carrier_id, date, customer,
 *     customerIndex, customers, searchString, ordini,
 *     filteredShipments, shipments, data, triggerSecondTable,
 *     canSelectAll, etc.),
 *   • dropped custom `deepCopy` → native `structuredClone()`,
 *   • dropped unused methods: `getOrderDate`, `openModalDamnIt`
 *     (alias of openBorderoModal), `onMessengerNameSelect`,
 *     `seeTheDate` (inlined in onStartDateChange).
 *   • LATENT BUG FIX — timer leak: the legacy `ngOnDestroy` cleared
 *     the route subscription but NOT the 60s `setInterval` that
 *     calls `getOpenOrders()`. Every nav-away leaked one timer.
 *     Now properly cleared on destroy.
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: loadOrdersToSign on init, search by term,
 * carrier dropdown filter → batch sign, batch select toggle,
 * completeOrder, confirm-complete sub-modal, bordero-page sub-modal,
 * detail modal.
 */
@Component({
  selector: 'app-ordini-da-firmare',
  templateUrl: './ordini-da-firmare.component.html',
  styleUrls: ['./ordini-da-firmare.component.scss'],
  standalone: false,
})


export class OrdiniDaFirmareComponent implements OnInit, OnDestroy {

  private readonly openOrdersServ = inject(OrdersService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterService = inject(SearchService);
  public readonly openModal = inject(OpenModalService);

  // ── Data ────────────────────────────────────────────────────────
  public orders: Orders.OrderSignature[] = [];
  public selectedOrders: Orders.OrderSelectedToSign[] = [];
  public carriers: string[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public filterTerm = '';
  public chosenCarrier: string | null = null;
  // Always stored as `string` because the sub-modals (detail,
  // confirm-complete) bind these to their `@Input() order_id: string`
  // — we normalise here to avoid TS2322 in the template.
  public clickedId = '';
  public clickedIdComplete = '';
  public isOneSelected = false;
  public areSelected = false;
  public readonly showAll = 'Mostra tutti';

  // ── Date range filter (defaults: 2021-01-01 → today) ───────────
  startDate: Date = new Date(2021, 0, 1);
  endDate: Date = new Date();

  filters = {
    dateOrginizedBy: {
      text: 'Data ordine',
      value: 'or_order_number',
    },
    startingDate: this.startDate as Date | null,
    endDate: this.endDate as Date,
    minDate: new Date(),
    maxDate: new Date(),
  };

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ── Internal: 60s auto-refresh timer ───────────────────────────
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly refreshInterval = 60_000;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    this.filterTerm = this.filterService.getFilterText() ?? '';

    if (this.filterTerm) {
      this.searchOrders(this.filterTerm);
      return;
    }

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (Object.keys(params).length === 0) {
          this.getOpenOrders();
          this.startAutoRefresh();
        } 
        else {
          const searchParam: string = params['search'] ?? '';
          const dateParam: string = params['data'];
          this.filters.startingDate = dateParam ? parseISO(dateParam) : null;
          this.searchOrders(searchParam);
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(
      () => this.getOpenOrders(),
      this.refreshInterval,
    );
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  getOpenOrders(): void {
    this.loading = true;
    this.openOrdersServ
      .getOrdersToSign()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData) => {
          if (!ordersData || ordersData.length === 0) {
            this.orders = [];
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.orders = ordersData;
          this.getCarrierList();
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private getCarrierList(): void {
    this.carriers = [
      ...new Set(
        this.orders
          .map((item) =>
            item.or_carrier_id != null
              ? item.or_carrier_desc
              : item.or_carrier_other,
          )
          .map((item) => (item === null ? '' : item)),
      ),
    ];
    this.carriers.unshift(this.showAll);
  }

  // ──────────────────────────────────────────────────────────────────
  // Search.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(searchString?: string): void {
    if (searchString !== undefined) this.filterTerm = searchString;
    const term = this.filterTerm?.trim() ?? '';
    this.openOrdersServ
      .searchOrdersToSign(term || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          this.orders = result?.data ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  /** Called after a search in sign-batch mode — preserves the
   *  carrier filter that was active before the search. */
  searchAfterCarrier(): void {
    const term = this.filterTerm?.trim() ?? '';
    this.openOrdersServ
      .searchOrdersToSign(term || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          this.orders = result?.data ?? [];
          this.changeCarier(this.chosenCarrier);
          this.cdr.detectChanges();
        },
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.filters.startingDate = this.startDate;
    this.filterService.clearFilterText();
    this.getOpenOrders();
  }

  handleFilterChange(): void {
    this.filterService.setFilterText(this.filterTerm);
  }

  onInputChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.validity && inputElement.validity.valueMissing) {
      this.getOpenOrders();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // View mode swap — list ↔ sign-batch.
  // ──────────────────────────────────────────────────────────────────

  /** Triggered when the user picks a carrier from the dropdown.
   *  Switches the view to sign-batch mode with checkbox-able
   *  selectedOrders. Passing null or `showAll` resets to list. */
  changeCarier(carrier: string | null): void {
    this.chosenCarrier = carrier ?? '';

    if (!carrier || carrier === this.showAll) {
      this.chosenCarrier = null;
      this.selectedOrders = [];
      this.areSelected = false;
      this.isOneSelected = false;
      return;
    }

    const copy = structuredClone(this.orders);
    this.selectedOrders = copy
      .filter((item) =>
        item.or_carrier_id
          ? item.or_carrier_desc === this.chosenCarrier
          : item.or_carrier_other === carrier,
      )
      .map((item, index): Orders.OrderSelectedToSign => ({
        order_id: item.order_id,
        or_customer: item.or_customer,
        op_checked: false,
        indexNo: index + 1,
        or_partial: item.or_partial,
        // Source `item.or_carrier_id` is typed as `boolean` in
        // OrderSignature but OrderSelectedToSign requires `string`
        // (model inconsistency older than this restyle — flagged for
        // Phase 2 cleanup). Cast preserves the legacy behaviour.
        or_carrier_id: item.or_carrier_id as unknown as string,
        or_carrier_desc: item.or_carrier_desc,
        or_carrier_other: item.or_carrier_other,
        order_number: item.or_order_number,
        or_boxes: item.op_articles_number,
      }));
    this.areSelected = false;
    this.isOneSelected = false;
  }

  /** Exits sign-batch mode and returns to the full list. */
  backToList(): void {
    this.changeCarier(null);
  }

  // ──────────────────────────────────────────────────────────────────
  // Sign-batch actions.
  // ──────────────────────────────────────────────────────────────────

  selectAllOrdersPerCarr(): void {
    this.areSelected = !this.areSelected;
    this.selectedOrders.forEach((item) => {
      item.op_checked = this.areSelected;
    });
    this.checkSelectedAll();
  }

  chooseItem(indexNo: number, event: Event): void {
    event.stopPropagation();
    const i = indexNo - 1;
    if (this.selectedOrders[i]) {
      this.selectedOrders[i].op_checked = !this.selectedOrders[i].op_checked;
    }
    this.checkSelectedAll();
  }

  private checkSelectedAll(): void {
    this.isOneSelected = this.selectedOrders.some(
      (item) => item.op_checked === true,
    );
  }

  /** Footer chip — count of checked orders. */
  get selectedCount(): number {
    return this.selectedOrders.filter((o) => o.op_checked).length;
  }

  // ──────────────────────────────────────────────────────────────────
  // Bordero generation flow.
  // ──────────────────────────────────────────────────────────────────

  openBorderoModal(): void {
    this.openModal.onBordero();
  }

  /** Called by app-bordero-page sub-modal (answerSubmitted event). */
  sign(courierName: string): void {
    const payload: Orders.OrderBordero = new Orders.OrderBordero();
    payload.order_date = new Date().toISOString();
    payload.courier_name = courierName;
    payload.orders = this.selectedOrders
      .filter((item) => item.op_checked === true)
      .map((item) => ({
        order_id: item.order_id,
        order_number:
          item.order_number?.substring(0, item.order_number.indexOf('/')) ??
          item.order_number,
      }));

    this.openOrdersServ
      .getBordero(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (borderoLink) => {
          if (!borderoLink?.success) return;
          this.router.navigate(['/firmare']);
        },
      });

    Swal.fire({
      icon: 'success',
      title: 'Generato!',
      text: 'Borderò generato con successo.',
      timer: 1500,
      timerProgressBar: true,
      showConfirmButton: false,
    });
    this.router.navigate(['/assign-order']);
  }

  // ──────────────────────────────────────────────────────────────────
  // Single-order actions.
  // ──────────────────────────────────────────────────────────────────

  openConfirmModal(orderId: string | number): void {
    this.clickedIdComplete = String(orderId);
    this.openModal.onConfirmComplete();
  }

  /** Called by app-confirm-complete sub-modal (modalClosed event). */
  completeOrder(orderId: string | number): void {
    this.clickedIdComplete = String(orderId);
    this.openOrdersServ
      .completeOrder(this.clickedIdComplete)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Completato!',
            text: 'Ordine completato con successo.',
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
          this.getOpenOrders();
        },
      });
  }

  gotoDetails(orderId: string | number): void {
    this.clickedId = String(orderId);
    this.openModal.daFirmareOn();
  }

  // ──────────────────────────────────────────────────────────────────
  // Native date input handlers.
  // ──────────────────────────────────────────────────────────────────

  onStartDateChange(value: Date | string | null): void {
    if (!value) {
      this.filters.startingDate = null;
      return;
    }
    this.filters.startingDate = value instanceof Date ? value : new Date(value);
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
  }

  get filteredOrders(): Orders.OrderSignature[] {
    const customer = this.chosenCarrier;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.orders.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if (start && o.or_order_date && new Date(o.or_order_date) < start) {
        return false;
      }
      if (end && o.or_order_date && new Date(o.or_order_date) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
